import { useEffect, useRef, useState } from "react";
import "./Table.css";
import TablePagination from "./TablePagination";
import TableControls from "./TableControls";
import TableHeader from "./TableHeader";
import TableBody from "./TableBody";
import TablePopover from "./TablePopover";
import Checkbox from "../checkbox/Checkbox";
import LoadingSpinner from "../loading/LoadingSpinner";
import ImgWithTitle from "../workspaceContainer/ImgWithTitle";
import { formatETATime, formatDateTime } from "../../config/utilities/fDate";
import { formatCapitalize } from "../../config/utilities/formatCapitalize";
import { useTimezone } from "../../config/utilities/useTimezone";
import { useAuth } from "../../config/utilities/useAuth";
import { useIsMobile } from "../../config/utilities/useBreakpoint";
import MenuIcon from "../../image/hamburgerDotMenu.svg";
import ClockIcon from "../../pages/serviceAnnouncement/img/clock.svg";
import ArrowIcon from "../../pages/serviceAnnouncement/img/arrow.svg";
import StarIcon from "../../pages/serviceAnnouncement/img/star.svg";
import InfoIcon from "../../pages/serviceAnnouncement/img/info.svg";
import AlertIcon from "../../pages/serviceAnnouncement/img/alert.svg";
import CheckIcon from "../../pages/serviceAnnouncement/img/checkMark.svg";
import { APP_ERROR_TOOLTIP_ID } from "../tooltip/tooltipIds";
import DispatchOccupancyCell from "./DispatchOccupancyCell";
import Badge from "../badge/Badge";
import { getTripDirection } from "../../config/utilities/scheduledSession";
import { DIRECTION_BADGE } from "../../config/utilities/tripBadge";
import { formatDispatchTripStatus } from "../../config/utilities/dispatchOccupancyHelpers";
import { getAuditDetailParts } from "../../config/utilities/auditLogHelpers";
import { formatCampaignAudienceSummary, inferCampaignTargetAudience } from "../../config/utilities/campaignAudiencePayload";
import { getAudienceLabel } from "../../pages/notifications/campaignFormHelpers";

const TRIP_TYPE_MISMATCH_TOOLTIP_HTML =
  "<span>This trip type no longer exists for the institution.</span>";

const resolveTripTypeDisplayName = (tripLike = {}, matchedTypeName) => {
  const candidates = [
    tripLike?.triptypeName,
    tripLike?.tripTypeName,
    tripLike?.typeName,
    tripLike?.tripType?.name,
    tripLike?.tripTypeLabel,
    matchedTypeName,
    tripLike?.type,
    tripLike?.tripType,
  ];

  const resolved = candidates.find(
    (value) => typeof value === "string" && value.trim().length > 0,
  );

  return resolved ? resolved.trim() : "";
};

export default function Table({
  loading,
  data,
  searchText,
  headers,
  showEmptyHeader,
  showFilter,
  showSearchBar,
  handlePopoverAction,
  onClick,
  module,
  showTableAction = true,
  useBackendPagination = false,
  currentPage,
  totalPages,
  onPageChange,
  filters,
  setSelectedFilters,
  selectedFilters,
  height,
  showEmptyRow = false,
  onSave,
  onCancel,
  optionData,
  emptyStateSize = "medium",
  draggable = false,
  onReorder,
  showTableControls = true,
  showPagination = true,
  totalRecords,
  searchTerm = "",
  setSearchTerm = () => {},
  onRowChange,
  onRowClick,
  isRowClickable,
  onBulkImport,
  onUpdateEmailStatus,
  enableMobileCards = true,
  density = "default",
}) {
  const { timezone } = useTimezone();
  const { tripTypes, privilege } = useAuth();
  const role = privilege?.role;
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [localSearch, setLocalSearch] = useState(searchTerm || "");
  const debouncedLocalSearch = useDebounce(localSearch, 300);
  const onPageChangeRef = useRef(onPageChange);
  const lastBackendQueryKeyRef = useRef(null);
  const [frontendPage, setFrontendPage] = useState(1);
  const [openPopover, setOpenPopover] = useState(null);
  const [scrolling, setScrolling] = useState(true);
  const isMobileViewport = useIsMobile();

  let rowsPerPage;

  switch (module) {
    case "stopsArray":
      rowsPerPage = data.length;
      break;
    case "parent":
      rowsPerPage = 3;
      break;
    case "child":
      rowsPerPage = 3;
      break;
    default:
      rowsPerPage = 10;
      break;
  }

  function useDebounce(value, delay = 500) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
      const handler = setTimeout(() => setDebouncedValue(value), delay);
      return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
  }

  useEffect(() => {
    setSearchTerm(debouncedLocalSearch);
  }, [debouncedLocalSearch, setSearchTerm]);

  useEffect(() => {
    onPageChangeRef.current = onPageChange;
  }, [onPageChange]);

  useEffect(() => {
    if (!useBackendPagination) {
      setFrontendPage(1);
      return;
    }

    if (typeof onPageChangeRef.current !== "function") return;

    let filtersKey = "";
    try {
      filtersKey = JSON.stringify(selectedFilters ?? null);
    } catch {
      filtersKey = "";
    }

    const queryKey = `${module}::${debouncedLocalSearch}::${filtersKey}`;
    if (lastBackendQueryKeyRef.current === queryKey) return;
    lastBackendQueryKeyRef.current = queryKey;

    switch (module) {
      case "family":
      case "admin":
        onPageChangeRef.current(1, debouncedLocalSearch);
        break;

      case "parent":
      case "child":
      case "stopsArray":
      default:
        onPageChangeRef.current(1, selectedFilters, debouncedLocalSearch);
        break;
    }
  }, [
    debouncedLocalSearch,
    module,
    selectedFilters,
    useBackendPagination,
  ]);

  const handleSearchChange = (e) => setLocalSearch(e.target.value);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedData = [...data].sort((a, b) => {
    if (sortConfig.key) {
      // If the key is `parent` or `child`, we need to handle the name fields specially
      let aValue = "";
      let bValue = "";

      // Handle sorting by `parent` or `child` name fields
      if (sortConfig.key === "parent" || sortConfig.key === "child") {
        // Combine firstName and lastName for sorting parents
        aValue = Array.isArray(a[sortConfig.key])
          ? `${a[sortConfig.key][0]?.firstName || ""} ${
              a[sortConfig.key][0]?.lastName || ""
            }`
          : "";
        bValue = Array.isArray(b[sortConfig.key])
          ? `${b[sortConfig.key][0]?.firstName || ""} ${
              b[sortConfig.key][0]?.lastName || ""
            }`
          : "";
      } else {
        // Default sorting for other fields (e.g., firstName in adminData)
        aValue = a[sortConfig.key]?.toLowerCase() || "";
        bValue = b[sortConfig.key]?.toLowerCase() || "";
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    }
    return 0;
  });

  const filteredData = !useBackendPagination
    ? sortedData.filter((row) => {
        const searchLower = searchTerm.toLowerCase();

        const fullName = `${row.firstName ?? ""} ${
          row.lastName ?? ""
        }`.toLowerCase();
        const matchesFullName = fullName.includes(searchLower);

        const matchesParent = row.parent?.some((p) =>
          `${p.firstName} ${p.lastName}`.toLowerCase().includes(searchLower)
        );

        const matchesChild = row.children?.some((c) =>
          `${c.firstName} ${c.lastName}`.toLowerCase().includes(searchLower)
        );

        const matchesName = row.name?.toLowerCase().includes(searchLower);
        const matchesTripName = row.tripName
          ?.toLowerCase()
          .includes(searchLower);
        const matchesVehicleNo = row.vehicleNumber
          ?.toLowerCase()
          .includes(searchLower);
        const matchesSchedule = row.tripDetails?.tripName
          ?.toLowerCase()
          .includes(searchLower);
        const matchesAttendance = row.childId
          ?.toLowerCase()
          .includes(searchLower);
        const matchesDispatch = row.vehicleDetails?.vehicleNumber
          ?.toLowerCase()
          .includes(searchLower);
        const matchesScheduleStudent = row.students?.some((s) =>
          s?.name?.toLowerCase()?.includes(searchLower)
        );
        return (
          matchesFullName ||
          matchesParent ||
          matchesChild ||
          matchesName ||
          matchesTripName ||
          matchesVehicleNo ||
          matchesSchedule ||
          matchesAttendance ||
          matchesDispatch ||
          matchesScheduleStudent
        );
      })
    : [];

  const totalPagesFrontend = !useBackendPagination
    ? Math.ceil(filteredData.length / rowsPerPage)
    : 0;

  const currentDataFrontend = !useBackendPagination
    ? filteredData.slice(
        (frontendPage - 1) * rowsPerPage,
        frontendPage * rowsPerPage
      )
    : [];

  const handlePreviousPage = () => {
    if (useBackendPagination && currentPage > 1) {
      if (module === "family" || module === "admin") {
        onPageChange(currentPage - 1, searchTerm);
      } else {
        onPageChange(currentPage - 1, selectedFilters, searchTerm);
      }
    } else if (!useBackendPagination && frontendPage > 1) {
      setFrontendPage(frontendPage - 1);
    }
  };

  const handleNextPage = () => {
    if (useBackendPagination && currentPage < totalPages) {
      if (module === "family" || module === "admin") {
        onPageChange(currentPage + 1, searchTerm);
      } else {
        onPageChange(currentPage + 1, selectedFilters, searchTerm);
      }
    } else if (!useBackendPagination && frontendPage < totalPagesFrontend) {
      setFrontendPage(frontendPage + 1);
    }
  };

  const handlePopoverToggle = (id) => {
    setOpenPopover((prevOpen) => {
      const nextOpen = prevOpen === id || id === null ? null : id;
      setScrolling(nextOpen === null);
      return nextOpen;
    });
  };

  const columns = headers.map((header) => header.key);

  const determineEmptyStateSize = (size) => {
    switch (size) {
      case "small":
        return {
          imgSize: "3rem",
          headerFontSize: "1.25rem",
          subTextFontSize: "0.75rem",
        };
      case "medium":
        return {
          imgSize: "6rem",
          headerFontSize: "1.5rem",
          subTextFontSize: "0.875rem",
        };
      case "large":
        return {
          imgSize: "9rem",
          headerFontSize: "1.75rem",
          subTextFontSize: "1rem",
        };
      default:
        return {
          imgSize: "6rem",
          headerFontSize: "1.875rem",
          subTextFontSize: "1rem",
        };
    }
  };
  const { imgSize, headerFontSize, subTextFontSize } =
    determineEmptyStateSize(emptyStateSize);

  const unsupportedMobileCardModules = new Set([
    "stopsArray",
    "associatedStudent",
  ]);
  const showMobileCards =
    enableMobileCards &&
    isMobileViewport &&
    !draggable &&
    !showEmptyRow &&
    !unsupportedMobileCardModules.has(module);

  const getDispatchStatusClass = (status) => {
    switch ((status || "").toLowerCase()) {
      case "ongoing":
        return "dispatch-status-badge dispatch-status-ongoing";
      case "completed":
        return "dispatch-status-badge dispatch-status-completed";
      case "at-final-stop":
        return "dispatch-status-badge dispatch-status-final-stop";
      default:
        return "dispatch-status-badge";
    }
  };

  const renderDispatchCard = (row, index) => {
    const registration = row.vehicleDetails?.busNumber
      ? `${row.vehicleDetails?.vehicleNumber} (${row.vehicleDetails?.busNumber})`
      : row.vehicleDetails?.vehicleNumber || "N/A";
    const nextEta = formatETATime(row.nextStop?.eta).formattedTime || "-";
    const finalEta = formatETATime(row.finalStop?.eta).formattedTime || "-";
    const statusText = formatDispatchTripStatus(row.status);
    const rowClickable =
      onRowClick && (!isRowClickable || isRowClickable(row));

    return (
      <div
        key={row.documentId || row.id || index}
        className={`table-mobile-card${rowClickable ? " clickable-row" : ""}`}
        onClick={rowClickable ? () => onRowClick(row) : undefined}
      >
        <div className="table-mobile-card-title">{registration}</div>
        <div className="table-mobile-card-row">
          <span className="table-mobile-card-label">Trip</span>
          <span className="table-mobile-card-value">{row.tripName || "-"}</span>
        </div>
        <div className="table-mobile-card-row">
          <span className="table-mobile-card-label">Status</span>
          <span className={getDispatchStatusClass(row.status)}>{statusText}</span>
        </div>
        <div className="table-mobile-card-row">
          <span className="table-mobile-card-label">Occupancy</span>
          <span className="table-mobile-card-value">
            <DispatchOccupancyCell
              occupancy={row.occupancy}
              tripStatus={row.status}
            />
          </span>
        </div>
        <div className="table-mobile-card-row">
          <span className="table-mobile-card-label">Previous Stop</span>
          <span className="table-mobile-card-value">{row.previousStop?.name || "-"}</span>
        </div>
        <div className="table-mobile-card-row">
          <span className="table-mobile-card-label">Next Stop</span>
          <span className="table-mobile-card-value">{row.nextStop?.name || "-"}</span>
        </div>
        <div className="table-mobile-card-row">
          <span className="table-mobile-card-label">Next ETA</span>
          <span className="table-mobile-card-value">{nextEta}</span>
        </div>
        <div className="table-mobile-card-row">
          <span className="table-mobile-card-label">Final Stop</span>
          <span className="table-mobile-card-value">{row.finalStop?.name || "-"}</span>
        </div>
        <div className="table-mobile-card-row">
          <span className="table-mobile-card-label">Final ETA</span>
          <span className="table-mobile-card-value">{finalEta}</span>
        </div>
      </div>
    );
  };

  const formatActiveInactiveForCard = (raw) => {
    if (raw === null || raw === undefined || raw === "") return null;
    if (typeof raw === "boolean") {
      return raw ? (
        <span className="table-mobile-status-active">Active</span>
      ) : (
        <span className="table-mobile-status-inactive">Inactive</span>
      );
    }

    const s = String(raw).trim().toLowerCase();
    if (s === "active") {
      return <span className="table-mobile-status-active">Active</span>;
    }
    if (s === "inactive") {
      return <span className="table-mobile-status-inactive">Inactive</span>;
    }
    return null;
  };

  const formatCardValue = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "string" || typeof value === "number") return String(value);
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (Array.isArray(value)) {
      if (!value.length) return "-";
      return value
        .map((item) => {
          if (typeof item === "string" || typeof item === "number") return item;
          if (item?.name) return item.name;
          if (item?.firstName || item?.lastName) {
            return `${item.firstName ?? ""} ${item.lastName ?? ""}`.trim();
          }
          return "";
        })
        .filter(Boolean)
        .join(", ");
    }
    if (typeof value === "object") {
      if (value?.name) return value.name;
      if (value?.vehicleNumber) return value.vehicleNumber;
      if (value?.tripName) return value.tripName;
      if (value?.firstName || value?.lastName) {
        return `${value.firstName ?? ""} ${value.lastName ?? ""}`.trim();
      }
    }
    return String(value);
  };

  const getCardValue = (row, key) => {
    const stripStopIdSuffix = (name) => {
      const text = String(name || "").trim();
      if (!text) return "";
      return text
        .replace(/\s*\((?:id[:\s]*)?[A-Za-z0-9-]{6,}\)\s*$/i, "")
        .trim();
    };

    const tripTypeColorMap = [
      { color: "#06AED4" }, // Scheduled
      { color: "#B93815" }, // On Demand
      { color: "#5925DC" }, // Published
      { color: "#0C4A6E" },
      { color: "#92400E" },
    ];

    const getTripTypeTextColor = (tripTypeId) => {
      if (!tripTypeId || tripTypeId === "none") return "#344054";
      const tripIndex = tripTypes?.findIndex((type) => type.id === tripTypeId);
      return tripTypeColorMap?.[tripIndex]?.color || "#344054";
    };

    const formatChildStopsValue = (rowData) => {
      const rawStops =
        rowData?.stops ??
        rowData?.stopData ??
        rowData?.associatedStops ??
        rowData?.stopName ??
        null;

      const stopItems = [];
      const seen = new Set();
      const pushStop = (name, session) => {
        const cleanedName = stripStopIdSuffix(name);
        if (!cleanedName) return;
        const dedupeKey = `${session || "none"}::${cleanedName}`;
        if (seen.has(dedupeKey)) return;
        seen.add(dedupeKey);
        stopItems.push({ name: cleanedName, session: session || "none" });
      };

      const extractStops = (value, sessionHint = "none") => {
        if (!value) return;
        if (typeof value === "string" || typeof value === "number") {
          pushStop(String(value), sessionHint);
          return;
        }

        if (Array.isArray(value)) {
          value.forEach((item) => extractStops(item, sessionHint));
          return;
        }

        if (typeof value === "object") {
          if (typeof value.name === "string" || typeof value.stopName === "string") {
            pushStop(
              value.name || value.stopName,
              value.session || value.tripSession || sessionHint,
            );
            return;
          }

          Object.entries(value).forEach(([objectKey, objectValue]) => {
            const nextSession = tripTypes?.some((type) => type.id === objectKey)
              ? objectKey
              : sessionHint;
            extractStops(objectValue, nextSession);
          });
        }
      };

      extractStops(rawStops);

      if (!stopItems.length) return "-";

      const sortedStops = stopItems.slice().sort((a, b) => {
        if (a.session === b.session) return 0;
        if (a.session === "none") return -1;
        if (b.session === "none") return 1;
        const aIndex = tripTypes?.findIndex((t) => t.id === a.session) ?? -1;
        const bIndex = tripTypes?.findIndex((t) => t.id === b.session) ?? -1;
        return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
      });

      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
          {sortedStops.map((stop, index) => (
            <span
              key={`${stop.session}-${stop.name}-${index}`}
              style={{
                color: getTripTypeTextColor(stop.session),
                fontWeight: 600,
                lineHeight: "1.2rem",
                whiteSpace: "normal",
                wordBreak: "break-word",
              }}
            >
              {stop.name}
            </span>
          ))}
        </div>
      );
    };

    const formatTimeToAmPm = (timeValue) => {
      if (!timeValue || typeof timeValue !== "string" || !timeValue.includes(":")) {
        return timeValue || "-";
      }
      const [hStr, mStr = "00"] = timeValue.split(":");
      const h = Number(hStr);
      if (Number.isNaN(h)) return timeValue;
      const period = h >= 12 ? "PM" : "AM";
      const hour12 = h % 12 || 12;
      return `${hour12}:${String(mStr).padStart(2, "0")} ${period}`;
    };

    const getSchedulingStatusColor = (status) => {
      const s = String(status || "").trim().toLowerCase();
      switch (s) {
        case "scheduled":
          return "#B54708";
        case "exempted":
          return "#363F72";
        case "pickup":
        case "pick up":
        case "pick_up":
        case "pick-up":
        case "picked up":
          return "#175CD3";
        case "dropoff":
        case "drop off":
        case "drop_off":
        case "drop-off":
          return "#027A48";
        case "failedtoboard":
        case "failed to board":
        case "failed_to_board":
        case "failed-to-board":
        case "failed":
          return "#B42318";
        case "ongoing":
        case "on going":
          return "#175CD3";
        case "completed":
        case "arrived":
          return "#027A48";
        case "ontime":
        case "on time":
          return "#175CD3";
        case "delayed":
          return "#B42318";
        default:
          return "#344054";
      }
    };

    const formatSchedulingStatusLabel = (status) => {
      const s = String(status || "").trim().toLowerCase();
      if (!s) return "-";
      if (s === "pickup" || s === "pick up" || s === "pick_up" || s === "pick-up")
        return "Picked Up";
      if (
        s === "dropoff" ||
        s === "drop off" ||
        s === "drop_off" ||
        s === "drop-off"
      )
        return "Drop Off";
      if (
        s === "failedtoboard" ||
        s === "failed to board" ||
        s === "failed_to_board" ||
        s === "failed-to-board"
      )
        return "Failed to Board";
      if (s === "ontime" || s === "on time") return "On Time";
      if (s === "ongoing" || s === "on going") return "Ongoing";
      return formatCapitalize(status) || "-";
    };

    if (key === "status") {
      const rawStatus =
        row[key] ??
        (typeof row.isActive === "boolean"
          ? row.isActive
            ? "active"
            : "inactive"
          : undefined);
      const activeInactive = formatActiveInactiveForCard(rawStatus);
      if (activeInactive) return activeInactive;
    }

    // Vehicles table: telemetry should show "Yes" or "-" (no "No").
    if (module === "vehicles" && key === "useTelematic") {
      const v = row?.[key];
      if (v === true) return "Yes";
      if (v === false || v === null || v === undefined || v === "") return "-";
      return v ? "Yes" : "-";
    }

    // Attendance table (mobile cards): mimic desktop badge text colors, but render plain text.
    if (module === "attendance" && key === "exempted") {
      const isExempted = row?.status === "Exempted";
      return (
        <div
          style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
          onClick={(e) => e.stopPropagation()}
        >
          <span
            style={{
              color: isExempted ? "#363F72" : "#B54708",
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            {isExempted ? "Exempted" : "Scheduled"}
          </span>
          <Checkbox
            checked={isExempted}
            onChange={() =>
              typeof handlePopoverAction === "function" &&
              handlePopoverAction(row.childId)
            }
            disabled={role === "VIEWER"}
          />
        </div>
      );
    }

    if (module === "attendance" && key === "status") {
      const raw = String(row?.status ?? "").trim();
      // Normalize: "dropOff" => "dropoff", "failed_to_board" => "faile d...".
      const normalized = raw.toLowerCase().replace(/[\s_-]/g, "");

      const statusText = (() => {
        switch (normalized) {
          case "pickup":
            return "Picked Up";
          case "dropoff":
            return "Dropped Off";
          case "failedtoboard":
            return "Failed to Board";
          case "exempted":
            return "Exempted";
          case "scheduled":
            return "Scheduled";
          default:
            return formatCapitalize(raw) || "-";
        }
      })();

      const statusColor = (() => {
        switch (normalized) {
          case "scheduled":
            return "#B54708";
          case "exempted":
            return "#363F72";
          case "pickup":
            return "#175CD3";
          case "dropoff":
            return "#027A48";
          case "failedtoboard":
            return "#B42318";
          default:
            return "#344054";
        }
      })();

      return (
        <span
          style={{
            color: statusColor,
            fontWeight: 600,
            display: "inline",
            whiteSpace: "nowrap",
          }}
        >
          {statusText}
        </span>
      );
    }

    if (
      (module === "busPolicy" || module === "generalPolicy") &&
      key === "effectiveDate"
    ) {
      if (!row?.effectiveDate) return "-";
      const { formattedDate } = formatDateTime(row.effectiveDate, timezone);
      return formattedDate || "-";
    }

    if (module === "serviceAnnouncement" && key === "titleColor") {
      const colorValue = String(row?.titleColor || "").trim();
      if (!colorValue) return "-";

      return (
        <span
          title={colorValue}
          style={{
            display: "inline-block",
            width: "0.7rem",
            height: "0.7rem",
            borderRadius: "999px",
            backgroundColor: colorValue,
            border: "1px solid #d0d5dd",
            verticalAlign: "middle",
          }}
        />
      );
    }

    if (module === "serviceAnnouncement" && key === "icon") {
      const serviceIcons = {
        alert: AlertIcon,
        checkMark: CheckIcon,
        info: InfoIcon,
        arrow: ArrowIcon,
        clock: ClockIcon,
        star: StarIcon,
      };
      const iconKey = String(row?.icon || "").trim();
      const iconSrc = serviceIcons[iconKey];
      if (!iconSrc) return "-";
      return (
        <img
          src={iconSrc}
          alt={iconKey}
          style={{ width: "1.25rem", height: "1.25rem", objectFit: "contain" }}
        />
      );
    }

    switch (module) {
      case "audit":
        if (key === "module") {
          const rawModule = row.module;
          const displayModule = rawModule === "childs" ? "children" : rawModule;
          return formatCapitalize(displayModule) || "-";
        }
        if (key === "actionType") {
          const rawActionType = row.actionType ?? "";
          const actionType = String(rawActionType).toUpperCase();
          const label = formatCapitalize(rawActionType) || "-";

          if (actionType === "CREATE") {
            return (
              <span className="table-mobile-audit-action-badge table-mobile-audit-action-create">
                {label}
              </span>
            );
          }

          if (actionType === "UPDATE") {
            return (
              <span className="table-mobile-audit-action-badge table-mobile-audit-action-update">
                {label}
              </span>
            );
          }

          if (actionType === "DELETE") {
            return (
              <span className="table-mobile-audit-action-badge table-mobile-audit-action-delete">
                {label}
              </span>
            );
          }

          return (
            <span className="table-mobile-audit-action-badge table-mobile-audit-action-unknown">
              {label}
            </span>
          );
        }
        if (key === "fieldsChanged") {
          const { primary, secondary } = getAuditDetailParts(row);
          return (
            <span className="table-mobile-card-datetime">
              <span className="table-mobile-card-datetime-date">{primary}</span>
              {secondary ? (
                <span className="table-mobile-card-datetime-time">
                  {secondary}
                </span>
              ) : null}
            </span>
          );
        }
        if (key === "user") {
          const name = row.performedBy?.name || "-";
          const role = row.performedBy?.role
            ? formatCapitalize(row.performedBy.role)
            : "";
          return (
            <span className="table-mobile-card-datetime">
              <span className="table-mobile-card-datetime-date">{name}</span>
              {role ? (
                <span className="table-mobile-card-datetime-time">{role}</span>
              ) : null}
            </span>
          );
        }
        if (key === "time") {
          if (!row?.timestamp) return "-";
          const { formattedDate, formattedTime } = formatDateTime(
            row.timestamp,
            timezone,
          );
          return (
            <span className="table-mobile-card-datetime">
              <span className="table-mobile-card-datetime-date">
                {formattedDate}
              </span>
              <span className="table-mobile-card-datetime-time">
                {formattedTime}
              </span>
            </span>
          );
        }
        return formatCardValue(row[key]);
      case "family":
        if (key === "child") {
          const childrenData = row.children || [];
          if (!childrenData.length) return "-";
          return (
            <div className="table-mobile-card-multi">
              {childrenData.map((child, index) => {
                const name =
                  `${child.firstName ?? ""} ${child.lastName ?? ""}`.trim() ||
                  "-";
                const direction = getTripDirection(
                  child.associatedStops,
                  tripTypes || [],
                );
                if (!direction) {
                  return (
                    <div key={index} className="table-mobile-card-multi-name">
                      {name}
                    </div>
                  );
                }
                const isTwoWay = direction === "Two-Way";
                return (
                  <div
                    key={index}
                    className="table-mobile-card-multi-name"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.4rem",
                      flexWrap: "wrap",
                    }}
                  >
                    <span>{name}</span>
                    <Badge
                      text={direction}
                      size="sm"
                      {...(isTwoWay
                        ? DIRECTION_BADGE.twoWay
                        : DIRECTION_BADGE.oneWay)}
                      width="fit-content"
                    />
                  </div>
                );
              })}
            </div>
          );
        }
        if (key === "address") {
          return row.parent?.[0]?.address ?? "N/A";
        }
        return formatCardValue(row[key]);
      case "notifications":
        if (key === "lastTriggered") {
          if (!row?.lastTriggered) return "-";
          const { formattedDate, formattedTime } = formatDateTime(
            row.lastTriggered,
            timezone,
          );
          return (
            <span className="table-mobile-card-datetime">
              <span className="table-mobile-card-datetime-date">
                {formattedDate}
              </span>
              <span className="table-mobile-card-datetime-time">
                {formattedTime}
              </span>
            </span>
          );
        }
        if (key === "scheduledTime") {
          if (!row?.alertTime?.scheduled?.isEnabled) return "-";
          const { formattedDate, formattedTime } = formatDateTime(
            row.alertTime.scheduled.datetime,
            timezone,
          );
          return (
            <span className="table-mobile-card-datetime">
              <span className="table-mobile-card-datetime-date">
                {formattedDate}
              </span>
              <span className="table-mobile-card-datetime-time">
                {formattedTime}
              </span>
            </span>
          );
        }
        if (key === "status") {
          const rawStatus = row[key] ?? "";
          const s = String(rawStatus).trim().toLowerCase();

          const color =
            s === "scheduled"
              ? "#B54708"
              : s === "on demand"
                ? "#175CD3"
                : s === "published"
                  ? "#027A48"
                  : "#344054";

          return (
            <span style={{ color, fontWeight: 600 }}>
              {formatCapitalize(rawStatus) || "-"}
            </span>
          );
        }
        if (key === "targetAudience") {
          const summary =
            row.targetAudienceSummary ||
            formatCampaignAudienceSummary(row) ||
            "—";
          const audienceType = inferCampaignTargetAudience(row);
          const typeLabel = getAudienceLabel(audienceType) || summary;
          if (summary === typeLabel || summary === "—") {
            return typeLabel;
          }
          if (summary.startsWith(`${typeLabel}:`)) {
            return summary;
          }
          return `${typeLabel}: ${summary}`;
        }
        return formatCardValue(row[key]);
      case "admin":
        if (key === "fullName") {
          return `${row.firstName ?? ""} ${row.lastName ?? ""}`.trim() || "-";
        }
        if (key === "role") {
          return formatCapitalize(row[key] || "-");
        }
        return formatCardValue(row[key]);
      case "drivers":
        if (key === "type") {
          const rawType = String(row?.[key] || "").trim().toLowerCase();
          const typeColor =
            rawType === "driver"
              ? "#B54708"
              : rawType === "assistant"
                ? "#175CD3"
                : "#344054";

          return (
            <span style={{ color: typeColor, fontWeight: 600 }}>
              {formatCapitalize(rawType) || "-"}
            </span>
          );
        }
        return formatCardValue(row[key]);
      case "parent":
      case "child":
        if (key === "fullName") {
          return `${row.firstName ?? ""} ${row.lastName ?? ""}`.trim() || "-";
        }
        if (module === "parent" && key === "emailOnboardStatus") {
          const raw = row?.[key];
          const normalized = typeof raw === "string" ? raw.toLowerCase().trim() : "";
          const isSent = normalized === "sent";
          return isSent ? (
            <span
              style={{
                display: "inline-block",
                width: "0.5rem",
                height: "0.5rem",
                borderRadius: "999px",
                backgroundColor: "#027A48",
                verticalAlign: "middle",
              }}
            />
          ) : (
            "-"
          );
        }
        if (key === "stopName") {
          return formatChildStopsValue(row);
        }
        return formatCardValue(row[key]);
      case "dispatch":
        if (key === "vehicleNumber") {
          return row.vehicleDetails?.busNumber
            ? `${row.vehicleDetails?.vehicleNumber} (${row.vehicleDetails?.busNumber})`
            : row.vehicleDetails?.vehicleNumber || "-";
        }
        if (key === "tripName") return row.tripName || "-";
        if (key === "prevStopData") return row.previousStop?.name || "-";
        if (key === "nextStopData") return row.nextStop?.name || "-";
        if (key === "nextStopArrivalData")
          return formatETATime(row.nextStop?.eta).formattedTime || "-";
        if (key === "finalStopData") return row.finalStop?.name || "-";
        if (key === "finalStopArrivalData")
          return formatETATime(row.finalStop?.eta).formattedTime || "-";
        if (key === "occupancyData") {
          return `${row.occupancy?.currentOccupancy ?? 0}/${row.occupancy?.maxCapacity ?? 0}`;
        }
        if (key === "tripStatus") return (row.status || "-").replace(/-/g, " ");
        return formatCardValue(row[key]);
      case "scheduling":
        if (key === "name") return row.tripDetails?.tripName || "-";
        if (key === "tripType") {
          const tripTypeId = row.tripDetails?.tripType;
          const matchedType = tripTypes?.find((type) => type.id === tripTypeId);
          const displayTripType =
            resolveTripTypeDisplayName(row.tripDetails, matchedType?.name) ||
            tripTypeId ||
            "-";

          // Match desktop text colors (TableRow's getTripBadgeProps()).
          const tripTypeColorMap = [
            { color: "#06AED4" }, // Scheduled
            { color: "#B93815" }, // On Demand
            { color: "#5925DC" }, // Published
            { color: "#0C4A6E" },
            { color: "#92400E" },
          ];

          const tripIndex = tripTypes?.findIndex((type) => type.id === tripTypeId);
          const color = tripTypeColorMap?.[tripIndex]?.color || "#344054";

          return (
            <span style={{ color, fontWeight: 600, whiteSpace: "nowrap" }}>
              {displayTripType}
            </span>
          );
        }
        if (key === "startTime") return formatTimeToAmPm(row.startTime);
        if (key === "driver") return row.driverDetails?.driverName || "-";
        if (key === "assistant") return row.assistantDetails?.assistantName || "-";
        if (key === "vehicle") return row.vehicleDetails?.vehicleNumber || "-";
        if (key === "totalStudents") {
          const scheduleStatus = String(row.status || "").toLowerCase();
          const summary = row.attendanceSummary || {};
          const getCount = (countKey) => Number(summary[countKey] ?? 0);

          const colors = {
            Scheduled: "#B54708",
            Exempted: "#363F72",
            pickUp: "#175CD3",
            dropOff: "#027A48",
            failedToBoard: "#B42318",
          };

          const Row = ({ label, count, color }) => (
            <span style={{ color, fontWeight: 600 }}>
              {label}: {count}
            </span>
          );

          if (scheduleStatus === "scheduled") {
            return (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                <Row
                  label="Scheduled"
                  count={getCount("Scheduled")}
                  color={colors.Scheduled}
                />
                <Row
                  label="Exempted"
                  count={getCount("Exempted")}
                  color={colors.Exempted}
                />
              </div>
            );
          }

          if (scheduleStatus === "ongoing") {
            return (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                <Row
                  label="Picked Up"
                  count={getCount("pickUp")}
                  color={colors.pickUp}
                />
                <Row
                  label="Failed"
                  count={getCount("failedToBoard")}
                  color={colors.failedToBoard}
                />
              </div>
            );
          }

          if (scheduleStatus === "completed") {
            return (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                <Row
                  label="Dropped Off"
                  count={getCount("dropOff")}
                  color={colors.dropOff}
                />
                <Row
                  label="Failed"
                  count={getCount("failedToBoard")}
                  color={colors.failedToBoard}
                />
                <Row
                  label="Exempted"
                  count={getCount("Exempted")}
                  color={colors.Exempted}
                />
              </div>
            );
          }

          // Fallback
          return (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
              <Row
                label="Scheduled"
                count={getCount("Scheduled")}
                color={colors.Scheduled}
              />
              <Row
                label="Exempted"
                count={getCount("Exempted")}
                color={colors.Exempted}
              />
            </div>
          );
        }
        if (key === "status") {
          return (
            <span
              style={{
                color: getSchedulingStatusColor(row.status),
                fontWeight: 600,
              }}
            >
              {formatSchedulingStatusLabel(row.status)}
            </span>
          );
        }
        return formatCardValue(row[key]);
      case "trips":
        if (key === "type") {
          const value = row?.[key];
          const matchedType = tripTypes?.find((type) => type.id === value);
          const showMissingTripTypeWarning = Boolean(value) && !matchedType;
          const displayText =
            resolveTripTypeDisplayName(row, matchedType?.name) || "-";

          // Match desktop colors from TableRow's `getTripBadgeProps()`
          const tripTypeColorMap = [
            { color: "#06AED4" }, // Scheduled
            { color: "#B93815" }, // On Demand
            { color: "#5925DC" }, // Published
            { color: "#0C4A6E" }, // (fallback for remaining types)
            { color: "#92400E" }, // (fallback for remaining types)
          ];

          const tripIndex = tripTypes?.findIndex((type) => type.id === value);
          const color =
            tripTypeColorMap?.[tripIndex]?.color || "#344054";

          if (showMissingTripTypeWarning) {
            return (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.35rem",
                }}
              >
                <span
                  style={{ color, fontWeight: 600, whiteSpace: "nowrap" }}
                >
                  {displayText}
                </span>
                <span
                  data-tooltip-id={APP_ERROR_TOOLTIP_ID}
                  data-tooltip-html={TRIP_TYPE_MISMATCH_TOOLTIP_HTML}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "1.125rem",
                    height: "1.125rem",
                    borderRadius: "999px",
                    background: "#FEF3F2",
                    color: "#B42318",
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    lineHeight: 1,
                    cursor: "help",
                    flexShrink: 0,
                  }}
                  aria-label="Trip type no longer exists for the institution"
                >
                  !
                </span>
              </div>
            );
          }

          return (
            <span style={{ color, fontWeight: 600, whiteSpace: "nowrap" }}>
              {displayText}
            </span>
          );
        }
        if (key === "startTime") return formatTimeToAmPm(row?.[key]);

        return formatCardValue(row?.[key]);
      default:
        if (key === "status") {
          return formatCapitalize(row[key]) ?? formatCardValue(row[key]);
        }
        return formatCardValue(row[key]);
    }
  };

  const getCardTitle = (row, index) => {
    if (module === "admin") {
      const fullName = `${row.firstName ?? ""} ${row.lastName ?? ""}`.trim();
      return fullName || `Record ${index + 1}`;
    }

    if (module === "parent" || module === "child") {
      const fullName = `${row.firstName ?? ""} ${row.lastName ?? ""}`.trim();
      return fullName || `Record ${index + 1}`;
    }

    if (module === "family") {
      const parents =
        row.parent?.map((p) => `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim()).filter(Boolean) ||
        [];
      return parents.length ? parents.join(", ") : `Record ${index + 1}`;
    }

    const titleHeader = headers[0];
    return titleHeader
      ? getCardValue(row, titleHeader.key)
      : row.name || row.tripName || row.vehicleNumber || `Record ${index + 1}`;
  };

  const getRowId = (row, index) => {
    if (module === "attendance") {
      return row?.childId ?? index;
    }
    return row?.id ?? row?.familyId ?? row?._id ?? row?.documentId ?? index;
  };

  const renderDefaultCard = (row, index) => {
    const cardTitle = getCardTitle(row, index);
    const rowId = getRowId(row, index);
    // Attendance mobile cards should not show the 3-dot menu.
    const showMobileRowActions =
      module !== "attendance" && typeof handlePopoverAction === "function";

    return (
      <div
        key={rowId}
        className={`table-mobile-card ${onRowClick ? "clickable-row" : ""}`}
        onClick={onRowClick ? () => onRowClick(row) : undefined}
      >
        <div className="table-mobile-card-header">
          <div className="table-mobile-card-title">{cardTitle}</div>

          {showMobileRowActions && (
            <TablePopover
              isOpen={openPopover === rowId}
              onClose={() => handlePopoverToggle(null)}
              rowId={rowId}
              row={row}
              handleAction={handlePopoverAction}
              module={module}
              canEdit={false}
              canView={true}
            >
              <button
                type="button"
                className="table-mobile-card-actions-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePopoverToggle(rowId);
                }}
                aria-label="Open actions"
              >
                <img
                  src={MenuIcon}
                  alt="Menu Icon"
                  className="table-mobile-card-menu-icon"
                />
              </button>
            </TablePopover>
          )}
        </div>

        {headers
          .slice(1)
          .filter((header) => !(module === "family" && header.key === "parent"))
          .map((header) => (
            <div className="table-mobile-card-row" key={header.key}>
              <span className="table-mobile-card-label">{header.name}</span>
              <div className="table-mobile-card-value">
                {getCardValue(row, header.key)}
              </div>
            </div>
          ))}
      </div>
    );
  };

  return (
    <div
      className={`table-container${module ? ` table-container--${module}` : ""}`}
      style={{ height: height }}
    >
      {showTableControls && (
        <TableControls
          showTableAction={showTableAction}
          searchTerm={localSearch}
          handleSearchChange={handleSearchChange}
          searchText={searchText}
          showFilter={showFilter}
          showSearchBar={showSearchBar}
          onClick={onClick}
          module={module}
          filters={filters}
          setSelectedFilters={setSelectedFilters}
          selectedFilters={selectedFilters}
          onBulkImport={onBulkImport}
          onUpdateEmailStatus={onUpdateEmailStatus}
        />
      )}

      <div
        className={`table-scroll ${scrolling ? "" : "no-scroll"} ${
          density === "compact" ? "table-density-compact" : ""
        }`}
      >
        {loading ? (
          <div className="table-loading-container">
            <LoadingSpinner
              className="loading-spinner"
              size="large"
              color="#ffb502"
            />
          </div>
        ) : useBackendPagination ? (
          data.length === 0 && showEmptyRow === false ? (
            <div className="table-empty-state">
              <ImgWithTitle
                titleText="No Data to Display"
                subText="There is nothing here right now. Try adjusting your filters or search, or add new entries."
                icon="agency"
                imgSize={imgSize}
                headerFontSize={headerFontSize}
                subTextFontSize={subTextFontSize}
              />
            </div>
          ) : showMobileCards ? (
            <div className="table-mobile-cards">
              {data.map((row, index) =>
                module === "dispatch"
                  ? renderDispatchCard(row, index)
                  : renderDefaultCard(row, index)
              )}
            </div>
          ) : (
            <table className={module ? `table--${module}` : undefined}>
              <TableHeader
                sortConfig={sortConfig}
                handleSort={handleSort}
                headers={headers}
                showEmptyHeader={showEmptyHeader}
                draggable={draggable}
                module={module}
              />
              <TableBody
                currentData={data}
                columns={columns}
                onPopoverToggle={handlePopoverToggle}
                openPopover={openPopover}
            handlePopoverAction={handlePopoverAction}
                module={module}
                onSave={onSave}
                onCancel={onCancel}
                optionData={optionData}
                showEmptyRow={showEmptyRow}
                draggable={draggable}
                onReorder={onReorder}
                onRowChange={onRowChange}
                onRowClick={onRowClick}
                isRowClickable={isRowClickable}
              />
            </table>
          )
        ) : filteredData.length === 0 && showEmptyRow === false ? (
          <div className="table-empty-state">
            <ImgWithTitle
              titleText="No Data to Display"
              subText="There is nothing here right now. Try adjusting your filters or search, or add new entries."
              icon="agency"
              imgSize={imgSize}
              headerFontSize={headerFontSize}
              subTextFontSize={subTextFontSize}
            />
          </div>
        ) : showMobileCards ? (
          <div className="table-mobile-cards">
            {currentDataFrontend.map((row, index) =>
              module === "dispatch"
                ? renderDispatchCard(row, index)
                : renderDefaultCard(row, index)
            )}
          </div>
        ) : (
          <table className={module ? `table--${module}` : undefined}>
            <TableHeader
              sortConfig={sortConfig}
              handleSort={handleSort}
              headers={headers}
              showEmptyHeader={showEmptyHeader}
              draggable={draggable}
              module={module}
            />
            <TableBody
              currentData={currentDataFrontend}
              columns={columns}
              onPopoverToggle={handlePopoverToggle}
              openPopover={openPopover}
              handlePopoverAction={handlePopoverAction}
              module={module}
              onSave={onSave}
              onCancel={onCancel}
              optionData={optionData}
              showEmptyRow={showEmptyRow}
              draggable={draggable}
              onReorder={onReorder}
              onRowChange={onRowChange}
              onRowClick={onRowClick}
              isRowClickable={isRowClickable}
            />
          </table>
        )}
      </div>
      {!loading &&
        showPagination &&
        (useBackendPagination ? data.length > 0 : filteredData.length > 0) && (
          <TablePagination
            currentPage={useBackendPagination ? currentPage : frontendPage}
            totalPages={useBackendPagination ? totalPages : totalPagesFrontend}
            handlePreviousPage={handlePreviousPage}
            handleNextPage={handleNextPage}
            hasData={
              useBackendPagination ? data.length > 0 : filteredData.length > 0
            }
            total={useBackendPagination ? totalRecords : filteredData?.length}
            module={module}
          />
        )}
    </div>
  );
}
